/* eslint-disable react/prop-types */
import { Divider, List, Menu } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import React, { ReactNode, useState } from 'react';
import { ChevronDown, ChevronUp, Icon, LogOut } from 'react-feather';
import { useNavigate } from 'react-router';

import { resetUser } from '@/auth/store/userSlice';
import { NavBar } from '@/componentsv2/NavBar';
import { ThemeToggle } from '@/componentsv2/ThemeToggle';
import { segmentTracking } from '@/core/analytics';
import { ProfileOnboardingTask } from '@/openapi';
import APP_ROUTES from '@/routes/APP_ROUTES';
import { useAppDispatch } from '@/store';

export type NavBarItem = {
  key: string;
  text: string | ReactNode;
  to?: string;
  divider?: boolean;
  icon?: string | Icon;
  className?: string;
  mobileOnly?: boolean;
  children?: NavBarItem[];
  onClick?: () => void;
  handleKeyDown?: (event: React.KeyboardEvent) => void;
  active?: boolean;
};

type UserData = {
  displayName: string | null;
  picture_url: string;
  username?: string;
};

interface HeaderProps {
  userData: UserData | null;
  incompleteTasks?: ProfileOnboardingTask[];
  SearchComponent?: React.ElementType;
  isAuthenticated: boolean;
  navbarItems: NavBarItem[];
  userMenuItems?: NavBarItem[];
  findExperts?: React.ReactNode;
  /**
   * Icon-tray slot between search and the user menu. Passed in rather than
   * imported so this chrome stays feature-agnostic and every test that renders
   * the header does not need the feature's providers.
   */
  notificationBell?: React.ReactNode;
  logout: (args: { returnTo: string }) => void;
}

function MoreOptionsNavbar({ navbarItems }: { navbarItems: NavBarItem[] }) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNavigate = (item: NavBarItem) => {
    if (item.onClick) {
      item.onClick();
    } else if (item.to) {
      navigate(item.to);
    }
  };

  if (!navbarItems.length) return null;
  if (navbarItems.length === 1) {
    const item = navbarItems[0];
    if (item.mobileOnly) return null;

    const NavIcon = item.icon && typeof item.icon !== 'string' ? item.icon : undefined;

    return (
      <NavBar.Item icon={NavIcon} path={item.to} onClick={() => handleNavigate(item)}>
        {item.text}
      </NavBar.Item>
    );
  }

  return (
    <div>
      <NavBar.Item icon={open ? ChevronUp : ChevronDown} onClick={handleClick} path={''}>
        More
      </NavBar.Item>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        classes={{ paper: 'px-5' }}
      >
        {navbarItems.map((item) => {
          if (item.mobileOnly) return;

          const NavIcon = item.icon && typeof item.icon !== 'string' ? item.icon : undefined;

          return (
            <NavBar.Item
              key={item.key}
              icon={NavIcon}
              path={item.to}
              onClick={() => handleNavigate(item)}
            >
              {item.text}
            </NavBar.Item>
          );
        })}
      </Menu>
    </div>
  );
}

export const Header: React.FC<HeaderProps> = ({
  userData,
  incompleteTasks,
  SearchComponent,
  isAuthenticated,
  navbarItems,
  userMenuItems,
  findExperts,
  notificationBell,
  logout,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOptionsOpen, setUserMenuOptionsOpen] = useState('');

  const handleMenuOpenChange = (open: boolean) => {
    setMenuOpen(open);
    if (!open) {
      setUserMenuOptionsOpen('');
    }
  };

  const handleLogout = () => {
    const segmentPayload = {
      event_type: 'Access',
      event_name: 'Log Out',
    };
    segmentTracking('clicked-logout-button', segmentPayload);
    dispatch(resetUser());
    logout({ returnTo: window.location.origin });
  };

  const toggleUserMenuOptions = (key: string) => {
    if (userMenuOptionsOpen === key) {
      setUserMenuOptionsOpen('');
    } else {
      setUserMenuOptionsOpen(key);
    }
  };

  const handleClickUserMenu = (item: NavBarItem) => {
    if (item.children) {
      toggleUserMenuOptions(item.key);
      return;
    }

    setMenuOpen(false);
    if (item.onClick) {
      item.onClick();
    } else if (item.to) {
      navigate(item.to);
    }
  };

  const ChevronIcon = ({ item }: { item: NavBarItem }) => {
    if (item.children) {
      return userMenuOptionsOpen === item.key ? (
        <ChevronUp size={20} className="shrink-0 text-brand-primary" />
      ) : (
        <ChevronDown size={20} className="shrink-0" />
      );
    }
    return null;
  };

  const { displayName: fullName, picture_url: pictureUrl } = userData || {};

  return (
    <NavBar
      mobileNavSlot={
        <>
          {navbarItems.map((item) => {
            const NavIcon = item.icon && typeof item.icon !== 'string' ? item.icon : undefined;
            return (
              <NavBar.MobileItem
                key={item.key}
                path={item.to}
                icon={NavIcon}
                className={item.className}
                onClick={() => handleClickUserMenu(item)}
              >
                {item.text}
              </NavBar.MobileItem>
            );
          })}
          {isAuthenticated && userMenuItems && (
            <>
              <Divider className="my-2" component="li" />
              {userMenuItems.map((item) => {
                const ItemIcon = item.icon && typeof item.icon !== 'string' ? item.icon : undefined;
                return item.children ? (
                  <div key={item.key}>
                    <NavBar.MobileItem
                      boxClassName={userMenuOptionsOpen === item.key ? '!text-brand-primary' : ''}
                      onClick={() => handleClickUserMenu(item)}
                    >
                      {item.text}
                      <ChevronIcon item={item} />
                    </NavBar.MobileItem>
                    <Collapse in={userMenuOptionsOpen === item.key} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {item.children.map((child) => (
                          <NavBar.MobileItem
                            key={child.key}
                            className="pl-20"
                            onClick={() => handleClickUserMenu(child)}
                          >
                            {child.text}
                          </NavBar.MobileItem>
                        ))}
                      </List>
                    </Collapse>
                  </div>
                ) : (
                  <NavBar.MobileItem
                    key={item.key}
                    icon={ItemIcon}
                    divider={item.divider}
                    onClick={() => handleClickUserMenu(item)}
                  >
                    {item.text}
                  </NavBar.MobileItem>
                );
              })}
              <li>
                <ThemeToggle />
              </li>
              <NavBar.MobileItem icon={LogOut} onClick={handleLogout}>
                Log Out
              </NavBar.MobileItem>
            </>
          )}
        </>
      }
    >
      {isAuthenticated ? (
        <>
          <NavBar.ItemContainer>
            {navbarItems.slice(0, 5).map((item) => {
              if (item.mobileOnly) return;

              const NavIcon = item.icon && typeof item.icon !== 'string' ? item.icon : undefined;

              return (
                <NavBar.Item
                  key={item.key}
                  icon={NavIcon}
                  path={item.to}
                  onClick={() => handleClickUserMenu(item)}
                  hasNotification={item.key === 'dashboard' && !!incompleteTasks?.length}
                >
                  {item.text}
                </NavBar.Item>
              );
            })}
            {navbarItems.length > 5 && (
              <MoreOptionsNavbar
                navbarItems={navbarItems.filter((item) => !item.mobileOnly).slice(5)}
              />
            )}
          </NavBar.ItemContainer>
          {!!SearchComponent && <SearchComponent />}
          {findExperts}
          {notificationBell}
          {fullName ? (
            <NavBar.UserMenu
              fullName={fullName}
              avatarPictureUrl={pictureUrl}
              open={menuOpen}
              onOpenChange={handleMenuOpenChange}
            >
              {userMenuItems?.map((item) => {
                const ItemIcon = item.icon && typeof item.icon !== 'string' ? item.icon : undefined;

                return item.children ? (
                  <div key={item.key}>
                    <NavBar.UserMenuItem
                      divider={userMenuOptionsOpen === '' && item.divider}
                      className="!rounded-t-lg bg-surface-elevated font-normal hover:bg-surface-secondary"
                      onClick={() => handleClickUserMenu(item)}
                    >
                      <div className="flex w-full items-center justify-between">
                        {item.text}
                        {userMenuOptionsOpen === item.key ? (
                          <ChevronUp
                            size={16}
                            className="ml-8 shrink-0 text-on-surface-secondary"
                          />
                        ) : (
                          <ChevronDown
                            size={16}
                            className="ml-8 shrink-0 text-on-surface-secondary"
                          />
                        )}
                      </div>
                    </NavBar.UserMenuItem>
                    <Collapse in={userMenuOptionsOpen === item.key} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding className="max-h-[50vh] overflow-y-auto">
                        {item.children.map((child, index) => (
                          <NavBar.UserMenuItem
                            key={child.key}
                            divider={index === item.children!.length - 1 && item.divider}
                            className="py-6 pl-24 font-normal"
                            onClick={() => handleClickUserMenu(child)}
                          >
                            {child.text}
                          </NavBar.UserMenuItem>
                        ))}
                      </List>
                    </Collapse>
                  </div>
                ) : (
                  <NavBar.UserMenuItem
                    onKeyDown={item.handleKeyDown}
                    key={item.key}
                    divider={item.divider}
                    icon={ItemIcon}
                    className={item.active ? 'font-medium text-brand-primary' : 'font-normal'}
                    onClick={() => handleClickUserMenu(item)}
                  >
                    {item.text}
                  </NavBar.UserMenuItem>
                );
              })}
              <ThemeToggle />
              <NavBar.UserMenuItem
                icon={LogOut}
                className="font-normal text-on-surface-muted"
                onClick={handleLogout}
              >
                Log Out
              </NavBar.UserMenuItem>
            </NavBar.UserMenu>
          ) : null}
        </>
      ) : (
        <NavBar.UserMenu fullName="Visitor" open={menuOpen} onOpenChange={setMenuOpen}>
          <NavBar.UserMenuItem onClick={() => navigate(APP_ROUTES.login)}>
            Log In
          </NavBar.UserMenuItem>
          <NavBar.UserMenuItem onClick={() => navigate(APP_ROUTES.signup)}>
            Sign Up
          </NavBar.UserMenuItem>
        </NavBar.UserMenu>
      )}
    </NavBar>
  );
};

export default Header;
